/**
 * InvoiceFormDialog — New / Edit / Copy for Sales Control › Invoice.
 * Converted 1:1 from Access `frmCtrlSalesQryInvoiceAdd`: 3-col form (Access Left
 * 3720 / 9744 / 15708 → col 1/2/3) grouped into Access gutter sections
 * (Contract Info · Invoice Info · Final Price · Provisional Price · Custom
 * Invoice · Customer Invoice · Receipt · Payment). Combos = Autocomplete (store
 * code). Number fields Standard (2dp, right-aligned), dates DD-MMM-YYYY.
 *
 * Access btnSave validation (errCheck): contract_ref required (5); exactly one of
 * No.Inv / No.VAT (2 both empty, 3 both filled); invoice_date required (4);
 * duplicate customs_inv_no (1) — handled by /access-suggest on the No.Inv blur.
 *
 * Cascades (Access *_AfterUpdate):
 *   inv_no_index → customs_inv_no="INV"+idx, customer_invoice_number="INV"+idx+".F", clear No.VAT
 *   vat_no_index → customs_inv_no="VAT"+idx, customer_invoice_number="VAT"+idx+".F", clear No.Inv
 *   contract_ref → snapshot (fx_auto / price_unit / contract_type / price / final_price_usd / grade / goods_description)
 *   fp_fx        → final_price_vnd = final_price_usd × 1000 / fp_fx
 *
 * Edit disables contract_ref / No.Inv / No.VAT (Access sets .Enabled=False);
 * Copy hydrates from the source row but takes a fresh suggested index, fields enabled.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '../../theme/responsive'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type InvLookupItem,
  type SaleInvoiceWrite,
  useCreateSaleInvoiceMutation,
  useUpdateSaleInvoiceMutation,
  useLazyGetSaleInvoiceFormQuery,
  useLazyGetInvContractSnapshotQuery,
  useLazySuggestInvoiceNoQuery,
  useLazyCheckInvoiceCustomsNoQuery,
  useInvLookupContractsQuery,
  useInvLookupBanksQuery,
  useInvLookupDeliveryOrdersQuery,
  useInvDoWeightsQuery,
} from '../../store/api/saleControlApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

type Mode = 'new' | 'edit' | 'copy'

interface Props {
  open: boolean
  mode: Mode
  invoiceId: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  contractRef: string
  toBank: string
  invNoIndex: string
  vatNoIndex: string
  customsInvNo: string
  customerInvoiceNumber: string
  invoiceDate: string
  blDate: string
  commercialInvDate: string
  goodsDescription: string
  othersDescription: string
  fpFx: string
  ppClosingPrice: string
  ppFx: string
  customerInvAdjust: string
  receiptWh: string
  customersCreditNo: string
  reclaim: string
  paidDetails: string
  // readonly display (contract snapshot + carried-through persisted values)
  noBag: string
  contractType: string
  contractFxAuto: string
  contractPriceUnit: string
  contractPrice: string
  contractGrade: string
  finalPriceUsd: string
  finalPriceVnd: string
  customerInvGw: string
  customerInvNw: string
  customInvGw: string
  customInvNw: string
  receiptGw: string
  receiptNw: string
  toBeReleased: string
  toBePaid: string
}

const EMPTY: Draft = {
  contractRef: '', toBank: '', invNoIndex: '', vatNoIndex: '', customsInvNo: '',
  customerInvoiceNumber: '', invoiceDate: '', blDate: '', commercialInvDate: '',
  goodsDescription: '', othersDescription: '', fpFx: '', ppClosingPrice: '', ppFx: '',
  customerInvAdjust: '', receiptWh: '', customersCreditNo: '', reclaim: '', paidDetails: '',
  noBag: '', contractType: '', contractFxAuto: '', contractPriceUnit: '', contractPrice: '',
  contractGrade: '', finalPriceUsd: '', finalPriceVnd: '', customerInvGw: '', customerInvNw: '',
  customInvGw: '', customInvNw: '', receiptGw: '', receiptNw: '', toBeReleased: '', toBePaid: '',
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const sectionTitleSx: SxProps<Theme> = {
  fontWeight: 700, fontSize: '0.8125rem', color: 'primary.main',
  mt: 1.5, mb: 0.75, pb: 0.25, borderBottom: '1px solid', borderColor: 'divider',
}
const grid3Sx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))

function withCurrent(options: InvLookupItem[], value: string, label?: string): InvLookupItem | null {
  if (!value) return null
  return options.find((o) => o.value === value) ?? { value, label: label || value }
}

function Combo(props: {
  label: string
  value: string
  options: InvLookupItem[]
  loading?: boolean
  required?: boolean
  disabled?: boolean
  error?: boolean
  onInputChange?: (q: string) => void
  onPick: (value: string) => void
}) {
  const { label, value, options, loading, required, disabled, error, onInputChange, onPick } = props
  // Đang gõ search (server-search) → KHÔNG inject sel vào options: nhánh
  // filterOptions={(x)=>x} hiển thị mọi options nên dòng inject (selected cũ)
  // sẽ lẫn vào kết quả dù không khớp text (bug "1 dòng đúng + dòng cũ").
  const [typing, setTyping] = useState('')
  const searching = !!onInputChange && !!typing.trim()
  const sel = withCurrent(options, value)
  const opts = !searching && sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  return (
    <Autocomplete
      size={SM}
      disabled={disabled}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '')}
      onInputChange={onInputChange ? (_e, v, reason) => {
        if (reason === 'input') { setTyping(v); onInputChange(v) }
        else setTyping('')
      } : undefined}
      getOptionLabel={(o) => (!o ? '' : o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`)}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={onInputChange ? (x) => x : undefined}
      renderInput={(params) => <TextField {...params} label={required ? `${label} *` : label} error={error} />}
    />
  )
}

export default function InvoiceFormDialog({ open, mode, invoiceId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  // Edit/Copy hydrate hits a view query → show a loading state until the API
  // response lands, then render the fields (avoid a flash of empty inputs).
  const [formLoading, setFormLoading] = useState(false)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  // Customs INV No trùng số cũ (nhánh VAT không auto-suggest được) → chặn Save + cảnh báo.
  const [dupNotice, setDupNotice] = useState<string | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  const [loadForm] = useLazyGetSaleInvoiceFormQuery()
  const [loadSnapshot] = useLazyGetInvContractSnapshotQuery()
  const [suggest] = useLazySuggestInvoiceNoQuery()
  const [checkCustomsNo] = useLazyCheckInvoiceCustomsNoQuery()
  const [createInv, { isLoading: creating }] = useCreateSaleInvoiceMutation()
  const [updateInv, { isLoading: updating }] = useUpdateSaleInvoiceMutation()
  const saving = creating || updating

  const [contractQ, setContractQ] = useState('')
  const skip = { skip: !open }
  const contracts = useInvLookupContractsQuery(contractQ || undefined, skip)
  const banks = useInvLookupBanksQuery(undefined, skip)

  // ── For DO subform (frmCtrlSalesQryInvoiceAdd_DOEdit_ver2): set of DOs. ──────
  const [doNos, setDoNos] = useState<string[]>([])
  const [doQ, setDoQ] = useState('')
  const doList = useInvLookupDeliveryOrdersQuery(doQ || undefined, skip)
  // doTouched = user vừa thêm/bớt DO (KHÔNG phải hydrate Edit/Copy) → mới cho phép
  // recompute các field weight/bl_date từ Total (Access do_no_AfterUpdate chỉ chạy
  // khi user chọn DO, KHÔNG chạy lúc mở form → giữ nguyên giá trị đã lưu khi Edit).
  const doTouched = useRef(false)
  const addDoNo = (v: string) => {
    const d = (v || '').trim()
    if (!d) return
    doTouched.current = true
    setDoNos((prev) => (prev.includes(d) ? prev : [...prev, d]))
  }
  const removeDoNo = (v: string) => {
    doTouched.current = true
    setDoNos((prev) => prev.filter((x) => x !== v))
  }
  // Per-DO weight breakdown (NW/GW Customer/Customs/Receipt + bags + do_date) cho bảng
  // "For DO" đã chọn — nguồn v_invoice_lst_do_all (như Access do_no_AfterUpdate DSum).
  const doWeights = useInvDoWeightsQuery(doNos, { skip: doNos.length === 0 })
  const doItems = doWeights.data?.items
  // Rows theo đúng thứ tự doNos + dòng Total (dùng cho bảng VÀ recompute field dưới).
  const doRows = useMemo(
    () =>
      doNos.map(
        (d) =>
          doItems?.find((x) => x.doNo === d) ?? {
            doNo: d, customerNw: 0, customerGw: 0, customsNw: 0, customsGw: 0,
            receiptNw: 0, receiptGw: 0, numberBags: 0, doDate: null,
          },
      ),
    [doNos, doItems],
  )
  const doTot = useMemo(
    () =>
      doRows.reduce(
        (a, r) => ({
          customerNw: a.customerNw + r.customerNw, customerGw: a.customerGw + r.customerGw,
          customsNw: a.customsNw + r.customsNw, customsGw: a.customsGw + r.customsGw,
          receiptNw: a.receiptNw + r.receiptNw, receiptGw: a.receiptGw + r.receiptGw,
          numberBags: a.numberBags + r.numberBags,
        }),
        { customerNw: 0, customerGw: 0, customsNw: 0, customsGw: 0, receiptNw: 0, receiptGw: 0, numberBags: 0 },
      ),
    [doRows],
  )

  const readonly = mode === 'edit'

  // Access do_no_AfterUpdate: khi user thêm/bớt DO → điền lại 6 field GW/NW (Customer/
  // Customs/Receipt) = tổng theo DO đã chọn, No. Bag = tổng bags, và bl_date = max(
  // invoice_date, do_date các DO). Chỉ chạy khi doTouched (bỏ qua hydrate) và query DO
  // đã xong (tránh điền 0 giữa lúc đang fetch).
  useEffect(() => {
    if (!doTouched.current) return
    if (doNos.length > 0 && (doWeights.isFetching || !doItems)) return
    setDraft((p) => {
      const dates = [p.invoiceDate, ...doRows.map((r) => r.doDate)].filter(
        (d): d is string => !!d && dayjs(d).isValid(),
      )
      const blDate = dates.length
        ? dates.reduce((mx, d) => (dayjs(d).isAfter(dayjs(mx)) ? d : mx)).slice(0, 10)
        : p.blDate
      return {
        ...p,
        customerInvGw: fmtStd(doTot.customerGw), customerInvNw: fmtStd(doTot.customerNw),
        customInvGw: fmtStd(doTot.customsGw), customInvNw: fmtStd(doTot.customsNw),
        receiptGw: fmtStd(doTot.receiptGw), receiptNw: fmtStd(doTot.receiptNw),
        noBag: fmtStd(doTot.numberBags),
        blDate,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doTot, doRows, doWeights.isFetching, doItems, doNos.length])

  // ── contract snapshot → readonly derived fields (contract_ref_AfterUpdate) ──
  // overwriteGoods=true only on a manual contract pick (Access contract_ref_AfterUpdate,
  // which sets goods_description from the contract). On Edit/Copy hydrate it must stay
  // false so the stored goods_description is preserved (btnEdit/btnCopy never re-derive it).
  const applySnapshot = async (contractRef: string, fpFx: string, overwriteGoods = false) => {
    if (!contractRef) return
    const snap = await loadSnapshot(contractRef).unwrap().catch(() => null)
    if (!snap) return
    const usd = snap.finalPriceUsd ?? 0
    const fx = parseNum(fpFx)
    // FIX (Access sai): final_price_vnd (VND/kg) = final_price_usd (USD/MT) × fp_fx
    // (VND/USD) / 1000 — nghịch đảo đúng của final_price_usd = contract_price×1000/fx.
    // Access dùng `fx*usd` (thừa ×1000) khi pick, `usd*1000/fx` khi đổi FX — cả 2 sai.
    const vnd = ((fx ?? 0) * usd) / 1000
    setDraft((p) => ({
      ...p,
      contractFxAuto: fmtStd(snap.contractFxAuto),
      contractPriceUnit: snap.contractPriceUnit ?? '',
      contractType: snap.contractType ?? '',
      contractPrice: fmtStd(snap.contractPrice),
      finalPriceUsd: fmtStd(usd),
      contractGrade: snap.contractGrade ?? '',
      ...(overwriteGoods ? { goodsDescription: snap.goodsDescription || p.goodsDescription } : {}),
      finalPriceVnd: fmtStd(vnd),
    }))
  }

  // ── Hydrate on open (deps: [open, mode, invoiceId] only) ──────────────────
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setRegenNotice(null)
    setDupNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    doTouched.current = false // hydrate không được kích recompute (giữ giá trị đã lưu)
    setDoNos([])
    setDoQ('')
    if (mode !== 'new') setFormLoading(true) // show loading immediately for Edit/Copy
    void (async () => {
      if (mode === 'new') {
        setFormLoading(false)
        const today = dayjs().format('YYYY-MM-DD')
        const idx = await suggest().unwrap().catch(() => null)
        if (cancelled) return
        const i = idx ? String(idx.index) : ''
        setDraft({
          ...EMPTY,
          invoiceDate: today,
          invNoIndex: i,
          customsInvNo: i ? `INV${i}` : '',
          customerInvoiceNumber: i ? `INV${i}.F` : '',
        })
        return
      }
      if (!invoiceId) { setFormLoading(false); return }
      const f = await loadForm(invoiceId).unwrap().catch(() => null)
      if (cancelled) return
      if (!f) { setFormLoading(false); return }
      const g = (k: string) => s((f as Record<string, unknown>)[k])
      const isCopy = mode === 'copy'
      // Copy: fresh suggested inv index; Edit: keep the stored index.
      let invNoIndex = g('invoiceNo')
      let vatNoIndex = g('vatNo')
      let customsInvNo = g('customsInvNo')
      let customerInvoiceNumber = g('customerInvoiceNumber')
      if (isCopy) {
        const idx = await suggest().unwrap().catch(() => null)
        if (cancelled) return
        const i = idx ? String(idx.index) : ''
        invNoIndex = i
        vatNoIndex = ''
        customsInvNo = i ? `INV${i}` : ''
        customerInvoiceNumber = i ? `INV${i}.F` : ''
      }
      const fpFx = fmtStd(g('fpFx'))
      setDraft({
        ...EMPTY,
        contractRef: g('contractRef'), toBank: g('toBank'),
        invNoIndex, vatNoIndex, customsInvNo, customerInvoiceNumber,
        // Access btnCopy copies the source invoice_date verbatim (not today).
        invoiceDate: g('invoiceDate').slice(0, 10),
        blDate: g('blDate').slice(0, 10), commercialInvDate: g('commercialInvDate').slice(0, 10),
        goodsDescription: g('goodsDescription'), othersDescription: g('othersDescription'),
        fpFx, ppClosingPrice: fmtStd(g('ppClosingPrice')), ppFx: fmtStd(g('ppFx')),
        customerInvAdjust: fmtStd(g('customerInvAdjust')), receiptWh: g('receiptWh'),
        customersCreditNo: g('customersCreditNo'), reclaim: g('reclaim'), paidDetails: g('paidDetails'),
        // Access btnCopy leaves the weight fields blank (all commented out in VBA);
        // only Edit carries them across.
        noBag: isCopy ? '' : fmtStd(g('noBag')),
        customerInvGw: isCopy ? '' : fmtStd(g('customerInvGw')), customerInvNw: isCopy ? '' : fmtStd(g('customerInvNw')),
        customInvGw: isCopy ? '' : fmtStd(g('customInvGw')), customInvNw: isCopy ? '' : fmtStd(g('customInvNw')),
        receiptGw: isCopy ? '' : fmtStd(g('receiptGw')), receiptNw: isCopy ? '' : fmtStd(g('receiptNw')),
        toBeReleased: fmtStd(g('toBeReleased')), toBePaid: fmtStd(g('toBePaid')),
      })
      // For DO set — Access btnEdit re-inserts each do_no into public_tmp_invoice_do,
      // but btnCopy clears the tmp table without re-inserting → Copy starts empty.
      const dl = (f as Record<string, unknown>).doNos
      setDoNos(isCopy || !Array.isArray(dl) ? [] : [...new Set(dl.map((x) => String(x)))])
      setFormLoading(false)
      // Recompute derived contract fields from the hydrated contract.
      void applySnapshot(g('contractRef'), fpFx)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, invoiceId])

  // Dismiss regen alert when the user edits the index away from the suggestion.
  useEffect(() => {
    if (regenNotice && draft.invNoIndex !== regenNotice.to) setRegenNotice(null)
  }, [draft.invNoIndex, regenNotice])

  // ── Cascades ──────────────────────────────────────────────────────────────
  const onInvIdxChange = (idx: string) => {
    setRegenNotice(null)
    setDupNotice(null)
    setDraft((p) => ({
      ...p,
      invNoIndex: idx,
      vatNoIndex: idx ? '' : p.vatNoIndex,
      customsInvNo: idx ? `INV${idx}` : p.vatNoIndex ? `VAT${p.vatNoIndex}` : '',
      customerInvoiceNumber: idx ? `INV${idx}.F` : p.vatNoIndex ? `VAT${p.vatNoIndex}.F` : '',
    }))
  }
  const onVatIdxChange = (idx: string) => {
    setDupNotice(null)
    setDraft((p) => ({
      ...p,
      vatNoIndex: idx,
      invNoIndex: idx ? '' : p.invNoIndex,
      customsInvNo: idx ? `VAT${idx}` : p.invNoIndex ? `INV${p.invNoIndex}` : '',
      customerInvoiceNumber: idx ? `VAT${idx}.F` : p.invNoIndex ? `INV${p.invNoIndex}.F` : '',
    }))
  }
  const onContractPick = (v: string) => { set('contractRef', v); void applySnapshot(v, draft.fpFx, true) }
  const onFpFxBlur = () => {
    const formatted = fmtStd(draft.fpFx)
    const usd = parseNum(draft.finalPriceUsd) ?? 0
    const fx = parseNum(formatted) ?? 0
    // FIX (Access sai): VND/kg = USD/MT × FX(VND/USD) / 1000 (xem applySnapshot).
    setDraft((p) => ({ ...p, fpFx: formatted, finalPriceVnd: fmtStd((usd * fx) / 1000) }))
  }

  // ── Duplicate check on No.Inv / No.VAT blur (customs_inv_no, /access-suggest) ──
  // Chống sài lại số cũ ở New/Copy: check CHÍNH customs_inv_no (INV.../VAT...).
  // INV trùng → tự lấy số kế tiếp (suggest). VAT không có suggest → cảnh báo + chặn
  // Save. BE create/update vẫn chặn cứng (errCheck=1) nên an toàn kể cả bỏ qua.
  const handleNoBlur = async (kind: 'inv' | 'vat') => {
    if (readonly) return
    const val = (kind === 'inv' ? draft.invNoIndex : draft.vatNoIndex).trim()
    if (!val) return
    const customsInvNo = `${kind === 'inv' ? 'INV' : 'VAT'}${val}`
    // readonly (=edit) đã return ở trên → nhánh này chỉ New/Copy, không loại self.
    const excludeId = undefined
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await checkCustomsNo({ customsInvNo, excludeId }).unwrap()
      if (!check.exists) {
        setDupNotice(null)
        return
      }
      if (kind === 'inv') {
        const next = await suggest().unwrap()
        const newVal = String(next.index || '').trim()
        if (newVal && newVal !== val) {
          onInvIdxChange(newVal)
          setRegenNotice({ from: val, to: newVal })
          snack.warning(`This Invoice No. is existed! New No. Inv suggested: INV${newVal}`)
        } else {
          snack.warning(`This Invoice No. is existed! (${customsInvNo})`)
        }
      } else {
        setDupNotice(`Customs INV No '${customsInvNo}' is existed — choose another No. VAT.`)
        snack.warning(`This Invoice No. is existed! (${customsInvNo})`)
      }
    } catch {
      // BE re-checks on save; do not poison UX.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  const contractEmpty = !draft.contractRef.trim()
  const dateEmpty = !draft.invoiceDate.trim()
  const invSet = !!draft.invNoIndex.trim()
  const vatSet = !!draft.vatNoIndex.trim()
  const exactlyOne = invSet !== vatSet
  const canSave = canEdit && !contractEmpty && !dateEmpty && exactlyOne && !saving && !precheckBusy && !dupNotice

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (precheckBusyRef.current) return
    if (!canSave) return
    const payload: SaleInvoiceWrite = {
      contractRef: draft.contractRef || null,
      toBank: draft.toBank || null,
      invoiceNo: draft.invNoIndex || null,
      vatNo: draft.vatNoIndex || null,
      customsInvNo: draft.customsInvNo || null,
      customerInvoiceNumber: draft.customerInvoiceNumber || null,
      invoiceDate: draft.invoiceDate || null,
      blDate: draft.blDate || null,
      commercialInvDate: draft.commercialInvDate || null,
      goodsDescription: draft.goodsDescription || null,
      othersDescription: draft.othersDescription || null,
      fpFx: parseNum(draft.fpFx),
      ppClosingPrice: parseNum(draft.ppClosingPrice),
      ppFx: parseNum(draft.ppFx),
      customerInvAdjust: parseNum(draft.customerInvAdjust),
      receiptWh: draft.receiptWh || null,
      customersCreditNo: draft.customersCreditNo || null,
      reclaim: draft.reclaim || null,
      paidDetails: draft.paidDetails || null,
      customerInvGw: parseNum(draft.customerInvGw),
      customerInvNw: parseNum(draft.customerInvNw),
      customInvGw: parseNum(draft.customInvGw),
      customInvNw: parseNum(draft.customInvNw),
      receiptGw: parseNum(draft.receiptGw),
      receiptNw: parseNum(draft.receiptNw),
      toBeReleased: parseNum(draft.toBeReleased),
      toBePaid: parseNum(draft.toBePaid),
      doNos,
    }
    try {
      if (mode === 'edit' && invoiceId) {
        await updateInv({ id: invoiceId, body: payload }).unwrap()
        snack.success(`Invoice ${draft.customsInvNo} updated`)
      } else {
        await createInv(payload).unwrap()
        snack.success(`Invoice ${draft.customsInvNo} added`)
      }
      onSaved()
      onClose()
    } catch (err) {
      const msg = getErrorMessage(err, 'Save invoice failed')
      // Duplicate slipped past the blur pre-check → suggest a fresh index inline.
      if (mode !== 'edit' && /exist|duplicate/i.test(msg)) {
        try {
          const next = await suggest().unwrap()
          const newVal = String(next.index || '').trim()
          const oldVal = draft.invNoIndex.trim()
          if (newVal && newVal !== oldVal) {
            onInvIdxChange(newVal)
            setRegenNotice({ from: oldVal, to: newVal })
            return
          }
        } catch { /* fallthrough */ }
      }
      snack.error(msg)
    }
  }

  const title = mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'

  const dateField = (key: keyof Draft, label: string, required?: boolean, disabled?: boolean) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      disabled={disabled}
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: { size: SM, fullWidth: true, required, error: !disabled && required && !(draft[key] as string), InputLabelProps: { shrink: true }, sx: disabled ? readonlyFieldSx : undefined } }}
    />
  )
  const numField = (key: keyof Draft, label: string, extraBlur?: () => void) => (
    <TextField
      label={label} size={SM} fullWidth type="text" value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      onBlur={() => (extraBlur ? extraBlur() : set(key, fmtStd(draft[key] as string) as Draft[typeof key]))}
      inputProps={numProps}
    />
  )
  const textField = (key: keyof Draft, label: string, extra?: object) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])} {...extra} />
  )
  const roField = (key: keyof Draft, label: string, numeric?: boolean) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx}
      inputProps={{ tabIndex: -1, ...(numeric ? { style: { textAlign: 'right' } } : {}) }} />
  )
  const cell = (col: number | string, row: number, node: React.ReactNode) => (
    <Box sx={{ gridColumn: { md: col }, gridRow: { md: row } }}>{node}</Box>
  )

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" noWrap title={`${title} — Outbound Logistics - Invoice`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700 }}>
          {title} — <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Outbound Logistics - </Box>Invoice
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {formLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
        <>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit invoices.</Alert>}
        {regenNotice && (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            No. Inv <b>{regenNotice.from}</b> was just created — suggested new code <b>{regenNotice.to}</b>.
          </Alert>
        )}
        {dupNotice && (
          <Alert severity="error" sx={{ mb: 1.25, py: 0.25 }}>{dupNotice}</Alert>
        )}

        {/* ── Invoice ID (Access T=36, readonly — always shown, blank until saved) ── */}
        <Box sx={grid3Sx}>
          {cell(1, 1, (
            <TextField label="Invoice ID" size={SM} fullWidth
              value={mode === 'edit' && invoiceId ? String(invoiceId) : ''}
              InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx}
              inputProps={{ style: { textAlign: 'right' }, tabIndex: -1 }} />
          ))}
        </Box>

        {/* ── Contract Info (Access T=372–1044) ── */}
        <Typography sx={sectionTitleSx}>Contract Info</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, (
            <Combo label="Contract Ref" value={draft.contractRef} required disabled={readonly} error={contractEmpty}
              options={contracts.currentData?.items ?? []} loading={contracts.isFetching} onInputChange={setContractQ}
              onPick={onContractPick} />
          ))}
          {cell(2, 1, dateField('blDate', 'B/L Date', false, true))}
          {cell(3, 1, dateField('commercialInvDate', 'Commercial Inv Date'))}
          {cell(1, 2, roField('contractType', 'Contract Ref - Type'))}
          {cell(2, 2, roField('contractFxAuto', 'Contract Ref - Fx auto', true))}
          {cell(3, 2, roField('contractPriceUnit', 'Contract Ref - Price Unit'))}
          {cell(1, 3, roField('contractPrice', 'Contract Ref - Price', true))}
          {cell(2, 3, roField('contractGrade', 'Contract Ref - Grade'))}
        </Box>

        {/* ── Invoice Info (Access T=1380–2964) ── */}
        <Typography sx={sectionTitleSx}>Invoice Info</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, (
            <TextField label="No. Inv" size={SM} fullWidth disabled={readonly} value={draft.invNoIndex}
              onChange={(e) => onInvIdxChange(e.target.value)} onBlur={() => void handleNoBlur('inv')}
              inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
              helperText={
                readonly ? 'No. Inv cannot be changed after creation.'
                  : precheckBusy ? 'Verifying No. Inv…'
                  : mode === 'copy' ? 'Suggested below; edit if needed.'
                  : 'Suggested code below; edit if needed.'
              } />
          ))}
          {cell(2, 1, (
            <TextField label="No. VAT" size={SM} fullWidth disabled={readonly} value={draft.vatNoIndex}
              onChange={(e) => onVatIdxChange(e.target.value)} onBlur={() => void handleNoBlur('vat')}
              inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
              error={(invSet && vatSet) || !!dupNotice}
              helperText={invSet && vatSet ? 'Enter only No. Inv or No. VAT.' : dupNotice ? 'Customs INV No already exists.' : undefined} />
          ))}
          {cell(1, 2, roField('customsInvNo', 'Customer Inv No.'))}
          {cell(2, 2, roField('customerInvoiceNumber', 'Custom Inv No.'))}
          {cell(3, 2, (
            <Combo label="Bank" value={draft.toBank} options={banks.data?.items ?? []} loading={banks.isFetching}
              onPick={(v) => set('toBank', v)} />
          ))}
          {cell(1, 3, dateField('invoiceDate', 'Invoice Date', true))}
          {cell(2, 3, roField('noBag', 'No. Bag', true))}
          {cell('1 / -1', 4, textField('goodsDescription', 'Goods Description', { multiline: true, minRows: 2 }))}
          {cell('1 / -1', 5, textField('othersDescription', 'Others', { multiline: true, minRows: 2 }))}
        </Box>

        {/* ── For DO (Access T=3600 subform frmCtrlSalesQryInvoiceAdd_DOEdit_ver2) ── */}
        <Typography sx={sectionTitleSx}>For DO</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, (
            <Autocomplete
              size={SM}
              disabled={!canEdit}
              blurOnSelect
              options={(doList.currentData?.items ?? []).filter(
                (o, i, arr) => !doNos.includes(o.value) && arr.findIndex((x) => x.value === o.value) === i,
              )}
              value={null}
              loading={doList.isFetching}
              onChange={(_e, v) => { if (v) { addDoNo(v.value); setDoQ('') } }}
              onInputChange={(_e, v, reason) => { if (reason === 'input') setDoQ(v) }}
              filterOptions={(x) => x}
              getOptionLabel={(o) => (!o ? '' : o.value === o.label ? String(o.value) : `${o.value} — ${o.label}`)}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => <TextField {...params} label="DO No" placeholder="Add delivery order…" />}
            />
          ))}
          <Box sx={{ gridColumn: { md: '1 / -1' }, minHeight: 24 }}>
            {doNos.length === 0 ? (
              <Typography variant="caption" color="text.secondary">No delivery orders attached.</Typography>
            ) : (
              (() => {
                // Bảng DO đã chọn: NW/GW từng cặp (Customer/Customs/Receipt) + bags,
                // theo do_no (v_invoice_lst_do_all), kèm dòng Total — user yêu cầu.
                // rows/tot lấy từ memo doRows/doTot (dùng chung với recompute field dưới).
                const rows = doRows
                const tot = doTot
                const numSx = { textAlign: 'right', whiteSpace: 'nowrap', px: 1, py: 0.5 } as const
                const grpSx = { textAlign: 'center', fontWeight: 700, borderLeft: '1px solid', borderColor: 'divider' } as const
                return (
                  <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" sx={{ '& td, & th': { fontSize: '0.78rem' } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>DO No</TableCell>
                          <TableCell colSpan={2} sx={grpSx}>Customer Inv</TableCell>
                          <TableCell colSpan={2} sx={grpSx}>Customs Inv</TableCell>
                          <TableCell colSpan={2} sx={grpSx}>Receipt</TableCell>
                          <TableCell rowSpan={2} sx={{ ...numSx, fontWeight: 700 }}>Bags</TableCell>
                          {canEdit && <TableCell rowSpan={2} sx={{ width: 36 }} />}
                        </TableRow>
                        <TableRow>
                          {(['NW', 'GW', 'NW', 'GW', 'NW', 'GW'] as const).map((h, i) => (
                            <TableCell key={i} sx={{ ...numSx, fontWeight: 700, ...(i % 2 === 0 ? { borderLeft: '1px solid', borderColor: 'divider' } : {}) }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((r) => (
                          <TableRow key={r.doNo} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.doNo}</TableCell>
                            <TableCell sx={numSx}>{fmtStd(r.customerNw)}</TableCell>
                            <TableCell sx={numSx}>{fmtStd(r.customerGw)}</TableCell>
                            <TableCell sx={numSx}>{fmtStd(r.customsNw)}</TableCell>
                            <TableCell sx={numSx}>{fmtStd(r.customsGw)}</TableCell>
                            <TableCell sx={numSx}>{fmtStd(r.receiptNw)}</TableCell>
                            <TableCell sx={numSx}>{fmtStd(r.receiptGw)}</TableCell>
                            <TableCell sx={numSx}>{r.numberBags.toLocaleString('en-US')}</TableCell>
                            {canEdit && (
                              <TableCell sx={{ p: 0, textAlign: 'center' }}>
                                <IconButton size="small" onClick={() => removeDoNo(r.doNo)} aria-label={`Remove ${r.doNo}`}>
                                  <CloseIcon fontSize="inherit" />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                        <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>Total ({rows.length})</TableCell>
                          <TableCell sx={numSx}>{fmtStd(tot.customerNw)}</TableCell>
                          <TableCell sx={numSx}>{fmtStd(tot.customerGw)}</TableCell>
                          <TableCell sx={numSx}>{fmtStd(tot.customsNw)}</TableCell>
                          <TableCell sx={numSx}>{fmtStd(tot.customsGw)}</TableCell>
                          <TableCell sx={numSx}>{fmtStd(tot.receiptNw)}</TableCell>
                          <TableCell sx={numSx}>{fmtStd(tot.receiptGw)}</TableCell>
                          <TableCell sx={numSx}>{tot.numberBags.toLocaleString('en-US')}</TableCell>
                          {canEdit && <TableCell />}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                )
              })()
            )}
          </Box>
        </Box>

        {/* ── Final Price (Access T=6444) ── */}
        <Typography sx={sectionTitleSx}>Final Price</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, numField('fpFx', 'FX', onFpFxBlur))}
          {cell(2, 1, roField('finalPriceUsd', 'Final Price (usd/mt)', true))}
          {cell(3, 1, roField('finalPriceVnd', 'Final Price (vnd/kg)', true))}
        </Box>

        {/* ── Provisional Price (Access T=6780) ── */}
        <Typography sx={sectionTitleSx}>Provisional Price</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, numField('ppClosingPrice', 'Closing Price'))}
          {cell(2, 1, numField('ppFx', 'FX'))}
        </Box>

        {/* ── Customs Invoice (Access T=7116) ── */}
        <Typography sx={sectionTitleSx}>Customs Invoice</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, roField('customInvGw', 'Gross weight', true))}
          {cell(2, 1, roField('customInvNw', 'Net weight', true))}
        </Box>

        {/* ── Customer Invoice (Access T=7452) ── */}
        <Typography sx={sectionTitleSx}>Customer Invoice</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, roField('customerInvGw', 'Gross weight', true))}
          {cell(2, 1, roField('customerInvNw', 'Net weight', true))}
          {cell(3, 1, numField('customerInvAdjust', 'Adjusted'))}
        </Box>

        {/* ── Receipt (Access T=7788) ── */}
        <Typography sx={sectionTitleSx}>Receipt</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, roField('receiptGw', 'Gross weight', true))}
          {cell(2, 1, roField('receiptNw', 'Net weight', true))}
          {cell(3, 1, textField('receiptWh', 'WH Receipt'))}
        </Box>

        {/* ── Released (Access T=8124) ── */}
        <Typography sx={sectionTitleSx}>Released</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, roField('toBeReleased', 'To be released', true))}
          {cell(2, 1, roField('toBePaid', 'To be paid', true))}
        </Box>

        {/* ── Others (Access T=8460–8796) ── */}
        <Typography sx={sectionTitleSx}>Others</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, textField('customersCreditNo', "Customer's Debit No"))}
          {cell(2, 1, textField('reclaim', 'Reclaim'))}
          {cell('1 / -1', 2, textField('paidDetails', 'Paid Detail', { multiline: true, minRows: 2 }))}
        </Box>
        </>
        )}
      </DialogContent>
    </Dialog>
  )
}
